import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { CalendarCheck, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { BUSINESS_NAME, CURRENCY_SYMBOL, DEFAULT_COUNTRY_CODE } from '../../config/brand';

const statuses = ['', 'pending', 'confirmed', 'checked_in', 'in_progress', 'completed', 'cancelled', 'no_show'];

function buildWhatsAppUrl(phone, booking) {
  const cleanPhone = (phone || '').replace(/[^0-9]/g, '');
  const waPhone = cleanPhone.startsWith(DEFAULT_COUNTRY_CODE) ? cleanPhone : `${DEFAULT_COUNTRY_CODE}${cleanPhone}`;
  const services = booking.service_names?.join(', ') || 'Grooming';
  const msg = `Hi ${booking.client_name}! Your ${BUSINESS_NAME} grooming appointment is confirmed.\n\nPet: ${booking.pet_name} (${booking.pet_breed})\nService: ${services}\nDate: ${booking.date}\nTime: ${booking.time_slot}\nGroomer: ${booking.staff_name}\nTotal: ${CURRENCY_SYMBOL}${booking.total_price}\n\nSee you soon! - ${BUSINESS_NAME}`;
  return `https://wa.me/${waPhone}?text=${encodeURIComponent(msg)}`;
}

export default function AdminBookings() {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const fetchBookings = async () => {
    setLoading(true);
    try {
      const params = filter && filter !== 'all' ? `?status=${filter}` : '';
      const res = await api.get(`/admin/bookings${params}`);
      setBookings(res.data);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { fetchBookings(); }, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/bookings/${id}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchBookings();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const statusColor = (s) => {
    const map = {
      pending: 'bg-amber-100 text-amber-800', confirmed: 'bg-blue-100 text-blue-800',
      checked_in: 'bg-indigo-100 text-indigo-800', in_progress: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800', cancelled: 'bg-red-100 text-red-800', no_show: 'bg-gray-100 text-gray-800',
    };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6" data-testid="admin-bookings-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#3D2418]" style={{ fontFamily: 'Manrope' }}>Bookings</h1>
          <p className="text-sm text-[#7A6B5E] mt-1">{bookings.length} booking{bookings.length !== 1 ? 's' : ''}</p>
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-48 rounded-lg" data-testid="booking-status-filter">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {statuses.filter(Boolean).map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="rounded-2xl border-[#E8DDD4] bg-white overflow-hidden" style={{ boxShadow: '0 2px 12px rgba(106,62,40,0.06)' }}>
        {loading ? (
          <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[#6A3E28] border-t-transparent" /></div>
        ) : bookings.length === 0 ? (
          <CardContent className="p-8 text-center"><CalendarCheck className="w-10 h-10 text-[#C48C68] mx-auto mb-3" /><p className="text-sm text-[#7A6B5E]">No bookings found</p></CardContent>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#FBF7F3]">
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Date & Time</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Client</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Pet</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Services</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Groomer</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Price</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Status</TableHead>
                  <TableHead className="text-xs font-semibold text-[#7A6B5E]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bookings.map(b => (
                  <TableRow key={b.appointment_id} data-testid={`booking-row-${b.appointment_id}`}>
                    <TableCell className="text-sm"><p className="font-medium">{b.date}</p><p className="text-xs text-[#A09387]">{b.time_slot}</p></TableCell>
                    <TableCell className="text-sm">{b.client_name}</TableCell>
                    <TableCell className="text-sm"><p>{b.pet_name}</p><p className="text-xs text-[#A09387]">{b.pet_breed}</p></TableCell>
                    <TableCell className="text-sm">{b.service_names?.join(', ')}</TableCell>
                    <TableCell className="text-sm">{b.staff_name}</TableCell>
                    <TableCell className="text-sm font-medium">&#8377;{b.total_price}</TableCell>
                    <TableCell><span className={`text-xs font-medium px-2.5 py-1 rounded-full ${statusColor(b.status)}`}>{b.status.replace('_', ' ')}</span></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Select value={b.status} onValueChange={(val) => updateStatus(b.appointment_id, val)}>
                          <SelectTrigger className="w-28 h-8 text-xs rounded-lg" data-testid={`status-select-${b.appointment_id}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {statuses.filter(Boolean).map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {b.client_phone && (
                          <a
                            href={buildWhatsAppUrl(b.client_phone, b)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Send booking confirmation on WhatsApp"
                            className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#25D366] hover:bg-[#1DA851] text-white transition-colors shrink-0"
                            data-testid={`whatsapp-send-${b.appointment_id}`}
                          >
                            <MessageCircle className="w-4 h-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
